import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '@/store';
import {
    selectAvailableStores,
    selectStoreId,
    selectHasStore,
    selectMerchantId,
    selectIsSuperAdmin,
    selectIsImpersonating,
    initializeAuth,
} from '@/store/slices/authSlice';
import { useStoreData } from '@/hooks/useStoreData';
import { useQuery } from '@tanstack/react-query';
import { adminApi } from '@/features/admin/api';
import {
    ChevronDown,
    Plus,
    Loader2,
    Check,
    Search,
} from 'lucide-react';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover';
import { useToast } from '@/hooks/use-toast';
import { setImpersonationRedirectHint } from '@/utils/impersonationHint';

/* ─── Helpers ────────────────────────────────────────────────── */

const getInitials = (name: string) =>
    name
        .split(/[\s\-_]+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((w) => w[0].toUpperCase())
        .join('');

const AVATAR_PALETTES = [
    { bg: '#EDE9FE', text: '#7C3AED' },
    { bg: '#DBEAFE', text: '#2563EB' },
    { bg: '#D1FAE5', text: '#059669' },
    { bg: '#FEF3C7', text: '#D97706' },
    { bg: '#FCE7F3', text: '#DB2777' },
    { bg: '#E0E7FF', text: '#4F46E5' },
    { bg: '#CFFAFE', text: '#0891B2' },
    { bg: '#FEE2E2', text: '#DC2626' },
];

const getPalette = (str: string) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    return AVATAR_PALETTES[Math.abs(hash) % AVATAR_PALETTES.length];
};

const formatUrl = (url: string) =>
    url
        .replace(/^https?:\/\//, '')
        .replace(/\/$/, '');

/* ─── Component ──────────────────────────────────────────────── */

const StoreSwitcher: React.FC = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const reduxStores = useAppSelector(selectAvailableStores);
    const currentStoreId = useAppSelector(selectStoreId);
    const currentMerchantId = useAppSelector(selectMerchantId);
    const hasStore = useAppSelector(selectHasStore);
    const isSuperAdmin = useAppSelector(selectIsSuperAdmin);
    const isImpersonating = useAppSelector(selectIsImpersonating);
    const { toast } = useToast();
    const { data: storeData } = useStoreData();

    const [isOpen, setIsOpen] = useState(false);
    const [switching, setSwitching] = useState(false);
    const [search, setSearch] = useState('');

    // For super admins, fetch stores for the current merchant from admin API
    const { data: adminMerchantData } = useQuery({
        queryKey: ['admin-merchant-detail', currentMerchantId],
        queryFn: () => adminApi.getMerchant(currentMerchantId!),
        enabled: isSuperAdmin && !!currentMerchantId,
        staleTime: 60_000,
    });

    // Build unified store list (include all stores — custom + shopify)
    const allStores = useMemo(() => {
        if (isSuperAdmin && adminMerchantData?.stores?.length) {
            return adminMerchantData.stores
                .map((s) => ({
                    storeId: s.storeId,
                    storeName: s.storeName,
                    storeUrl: s.storeUrl,
                    status: s.status,
                    platform: s.platform,
                    merchantId: currentMerchantId || '',
                }));
        }
        // Filter redux stores to current merchant
        const merchantStores = currentMerchantId
            ? reduxStores.filter((s) => !s.merchantId || s.merchantId === currentMerchantId)
            : reduxStores;

        // Fallback: if redux has no stores for this merchant but storeData exists, use it
        if (merchantStores.length === 0 && storeData?.store) {
            const s = storeData.store;
            return [{
                storeId: s.storeId,
                storeName: s.storeName,
                storeUrl: s.storeUrl,
                status: s.status || 'active',
                platform: s.platform || 'shopify',
                merchantId: s.merchantId || currentMerchantId || '',
            }];
        }

        return merchantStores;
    }, [isSuperAdmin, adminMerchantData, reduxStores, currentMerchantId, storeData]);

    const currentStore = allStores.find((s) => s.storeId === currentStoreId);
    const isCurrentCustom = currentStore?.platform === 'custom'
        || storeData?.store?.platform === 'custom';
    const displayStoreName = isCurrentCustom
        ? (storeData?.store?.merchantDetails?.businessName || currentStore?.storeName || 'My Business')
        : (currentStore?.storeName
            || storeData?.store?.storeName
            || (currentStoreId ? 'Store' : 'Select store'));
    const displayStoreUrl = isCurrentCustom
        ? ''
        : (currentStore?.storeUrl || storeData?.store?.storeUrl || '');

    const filteredStores = useMemo(
        () =>
            allStores.filter(
                (s) =>
                    s.storeName.toLowerCase().includes(search.toLowerCase()) ||
                    s.storeUrl.toLowerCase().includes(search.toLowerCase())
            ),
        [allStores, search]
    );

    // Component always renders — even single-store users can see "Connect New Store"

    const handleSwitch = async (storeId: string) => {
        if (storeId === currentStoreId) {
            setIsOpen(false);
            return;
        }

        setSwitching(true);
        try {
            if (isSuperAdmin && currentMerchantId) {
                // Super admin: use impersonate API to switch store
                const targetStore = allStores.find((s) => s.storeId === storeId);
                if (targetStore) {
                    localStorage.setItem('impersonation_context', JSON.stringify({
                        merchant: null,
                        store: {
                            storeId: targetStore.storeId,
                            merchantId: currentMerchantId,
                            storeName: targetStore.storeName,
                            storeUrl: targetStore.storeUrl,
                            status: targetStore.status,
                            platform: targetStore.platform,
                        },
                    }));
                }
                await adminApi.impersonate({ merchantId: currentMerchantId, storeId });
                await dispatch(initializeAuth()).unwrap();
                setIsOpen(false);
                setImpersonationRedirectHint();
                window.location.href = '/';
            } else {
                // Regular user: use switchContext
                const { switchContext } = await import('@/store/slices/authSlice');
                const merchantId = currentMerchantId || undefined;
                await dispatch(switchContext({ storeId, merchantId })).unwrap();
                setIsOpen(false);
                window.location.href = '/';
            }
        } catch (error: any) {
            console.error('Failed to switch store:', error);
            toast({
                title: 'Operation Failed',
                description: typeof error === 'string' ? error : error?.message || 'Failed to switch store',
                variant: 'destructive',
            });
        } finally {
            setSwitching(false);
        }
    };

    /* No real store connected — for regular users, show connect prompt */
    if ((allStores.length === 0 || !hasStore) && !isSuperAdmin && !isImpersonating) {
        return (
            <div className="px-3 py-2">
                <button
                    onClick={() => navigate('/settings/integrations?connect=true')}
                    className="group w-full flex items-center gap-2.5 px-3 py-2.5
                        border border-gray-200 rounded-xl
                        hover:border-gray-300 hover:shadow-sm
                        active:scale-[.98]
                        transition-all duration-200"
                >
                    <div
                        className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0
                            bg-gray-100 text-gray-500
                            transition-transform duration-200
                            group-hover:scale-105"
                    >
                        <Plus className="w-4 h-4" />
                    </div>

                    <div className="flex-1 min-w-0 text-left">
                        <p className="text-[13px] font-[600] text-[#303030] truncate leading-tight">
                            Connect a store
                        </p>
                        <p className="text-[11px] text-gray-400 truncate leading-tight mt-0.5">
                            Link your Shopify store
                        </p>
                    </div>
                </button>
            </div>
        );
    }

    const palette = displayStoreName && displayStoreName !== 'Select store'
        ? getPalette(displayStoreName)
        : AVATAR_PALETTES[0];

    return (
        <div className="px-3 py-2">
            <Popover
                open={isOpen}
                onOpenChange={(open) => {
                    setIsOpen(open);
                    if (!open) setSearch('');
                }}
            >
                <PopoverTrigger asChild>
                    <button
                        className="group w-full flex items-center gap-2.5 px-3 py-2.5
                            border border-gray-200 rounded-xl
                            hover:border-gray-300 hover:shadow-sm
                            active:scale-[.98]
                            transition-all duration-200"
                        disabled={switching}
                    >
                        {/* Store avatar */}
                        <div
                            className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0
                                text-[12px] font-[700] tracking-tight transition-transform duration-200
                                group-hover:scale-105"
                            style={{
                                backgroundColor: palette.bg,
                                color: palette.text,
                            }}
                        >
                            {getInitials(displayStoreName || '?')}
                        </div>

                        {/* Name + URL */}
                        <div className="flex-1 min-w-0 text-left">
                            <p className="text-[13px] font-[600] text-[#303030] truncate leading-tight">
                                {displayStoreName}
                            </p>
                            {displayStoreUrl && (
                                <p className="text-[11px] text-gray-400 truncate leading-tight mt-0.5">
                                    {formatUrl(displayStoreUrl)}
                                </p>
                            )}
                        </div>

                        {/* Chevron / Loader */}
                        {switching ? (
                            <Loader2 className="w-4 h-4 animate-spin text-gray-400 flex-shrink-0" />
                        ) : (
                            <ChevronDown
                                className="w-4 h-4 text-gray-300 flex-shrink-0
                                    group-hover:text-gray-500 transition-colors duration-200"
                            />
                        )}
                    </button>
                </PopoverTrigger>

                <PopoverContent
                    className="w-[260px] p-0 rounded-xl shadow-lg border border-gray-200 overflow-hidden"
                    align="start"
                    side="top"
                    sideOffset={6}
                >
                    {/* Header */}
                    <div className="px-3.5 pt-3 pb-2">
                        <p className="text-[11px] font-[600] text-gray-400 uppercase tracking-wider">
                            Switch Store
                        </p>
                    </div>

                    {/* Search (show only when 4+ stores) */}
                    {allStores.length >= 4 && (
                        <div className="px-3 pb-2">
                            <div className="relative">
                                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                                <input
                                    type="text"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder="Search stores…"
                                    className="w-full pl-8 pr-3 py-1.5 text-[13px] bg-gray-50 border border-gray-200
                                        rounded-lg placeholder:text-gray-300
                                        focus:outline-none focus:ring-1 focus:ring-[#303030] focus:border-[#303030]
                                        transition-all duration-150"
                                    autoFocus
                                />
                            </div>
                        </div>
                    )}

                    {/* Store list */}
                    <div className="max-h-[280px] overflow-y-auto px-1.5 pb-1.5 custom-scrollbar">
                        {filteredStores.length === 0 && (
                            <p className="text-center text-[13px] text-gray-400 py-6">
                                No stores found
                            </p>
                        )}

                        {filteredStores.map((storeItem) => {
                            const isActive = storeItem.storeId === currentStoreId;
                            const isCustom = storeItem.platform === 'custom';
                            const itemDisplayName = isCustom
                                ? (storeData?.store?.merchantDetails?.businessName || storeItem.storeName)
                                : storeItem.storeName;
                            const sp = getPalette(itemDisplayName);
                            return (
                                <button
                                    key={storeItem.storeId}
                                    onClick={() => handleSwitch(storeItem.storeId)}
                                    className={`group/item w-full flex items-center gap-2.5 px-2.5 py-2.5 rounded-lg
                                        transition-all duration-150 ${isActive
                                            ? 'bg-[#F5F5F5]'
                                            : 'hover:bg-[#FAFAFA]'
                                        }`}
                                >
                                    {/* Miniature avatar */}
                                    <div
                                        className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0
                                            text-[11px] font-[700] tracking-tight"
                                        style={{
                                            backgroundColor: sp.bg,
                                            color: sp.text,
                                        }}
                                    >
                                        {getInitials(itemDisplayName)}
                                    </div>

                                    {/* Info */}
                                    <div className="flex-1 min-w-0 text-left">
                                        <p
                                            className={`text-[13px] truncate leading-tight ${isActive
                                                ? 'font-[650] text-[#303030]'
                                                : 'font-[500] text-gray-600 group-hover/item:text-[#303030]'
                                                }`}
                                        >
                                            {itemDisplayName}
                                        </p>
                                        {!isCustom && (
                                            <p className="text-[11px] text-gray-400 truncate leading-tight mt-0.5">
                                                {formatUrl(storeItem.storeUrl)}
                                            </p>
                                        )}
                                    </div>

                                    {/* Active indicator */}
                                    {isActive && (
                                        <Check className="w-4 h-4 text-green-600 flex-shrink-0" strokeWidth={2.5} />
                                    )}
                                </button>
                            );
                        })}
                    </div>

                    {/* Divider + Connect new store */}
                    {!isSuperAdmin && (
                        <div className="border-t border-gray-100">
                            <button
                                onClick={() => {
                                    setIsOpen(false);
                                    navigate('/settings/integrations?connect=true');
                                }}
                                className="group/add w-full flex items-center gap-2.5 px-4 py-3
                                    text-gray-500 hover:text-[#303030]
                                    hover:bg-[#FAFAFA] transition-all duration-150"
                            >
                                <div
                                    className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center
                                        flex-shrink-0 group-hover/add:bg-gray-200 transition-colors duration-200"
                                >
                                    <Plus className="w-3.5 h-3.5 text-gray-500" />
                                </div>
                                <span className="text-[13px] font-[500] text-gray-600 group-hover/add:text-[#303030]">
                                    Connect New Store
                                </span>
                            </button>
                        </div>
                    )}
                </PopoverContent>
            </Popover>
        </div>
    );
};

export default StoreSwitcher;
